import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContract,
  deleteContract,
  getAllContracts,
  getAllCurrency,
  getBilligDefaultContract,
  getBillingContractDropDownData,
  getContractById,
  getContractsByContractId,
  getServiceEntries,
  getServicesByContractId,
  reassignContract,
  updateContract,
  updateServiceContractById,
  updateServiceBillingSettings,
  bulkDeleteContract,
  contractStatusBulkUpdate,
} from "controller/api";
import {
  ApiErrorType,
  BulkDeleteContractInput,
  ContractStatusBulkUpdateInput,
  CreateNewContractInput,
  GetContractInput,
  GetServiceEntriesInput,
  ReassignContractInput,
  UpdateContractInput,
} from "shared";
import { sentryLogger } from "utilz";
import { debugConsole } from "utilz/helpers";

export const useGetAllCurrency = () => {
  return useQuery({
    queryKey: ["currency"],
    queryFn: () => getAllCurrency(),
    onError: (err) => debugConsole(err),
    staleTime: Infinity,
  });
};

export const useGetAllContracts = (data: GetContractInput) => {
  return useQuery({
    queryKey: [
      "contracts",
      data.contractStatus,
      data.contractType,
      data.customers,
      data.pageNumber,
      data.perPageCount,
      data.sortColumn,
      data.sortDirection,
      data.searchQuery,
      data.contractStartDate,
      data.contractEndDate,
      data.contractStartDateRange,
      data.contractEndDateRange,
      data.includeItems,
    ],
    queryFn: () => getAllContracts(data),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "billing",
        level: "error",
        functionName: "getAllContracts",
      }),
    enabled: data.customers ? true : false,
    staleTime: Infinity,
  });
};

export const useGetContractById = (id?: string | null | undefined) => {
  return useQuery({
    queryKey: ["contracts", id],
    queryFn: () => getContractById(id || ""),
    onError: (err) => debugConsole(err),
    enabled: id ? true : false,
  });
};

export const useGetContractByIdMutation = () => {
  return useMutation((id: string) => getContractById(id), {
    onSuccess(data, variables, context) {},
  });
};

export const useCreateContract = () => {
  const queryClient = useQueryClient();
  return useMutation(createContract, {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["contracts"]);
      queryClient.invalidateQueries(["readyToBill"]);
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "billing",
        level: "error",
        functionName: "createContract",
      }),
  });
};

export const useUpdateContract = () => {
  const queryClient = useQueryClient();
  return useMutation((value: UpdateContractInput) => updateContract(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["contracts"]);
      queryClient.invalidateQueries(["readyToBill"]);
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "billing",
        level: "error",
        functionName: "updateContract",
      }),
  });
};

export const useUpdateContractStatusBulk = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: ContractStatusBulkUpdateInput) => contractStatusBulkUpdate(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["contracts"]);
        queryClient.invalidateQueries(["readyToBill"]);
      },
      onError: (error: ApiErrorType) =>
        sentryLogger({
          error: error,
          workflow: "billing",
          level: "error",
          functionName: "contractStatusBulkUpdate",
        }),
    }
  );
};

export const useGetBillingContractDropDownData = (
  customerId: string,
  serviceType: string
) => {
  return useQuery({
    enabled: !!customerId,
    queryKey: [
      "contracts",
      "billingContractDropDownData",
      customerId,
      serviceType,
    ],
    queryFn: () => getBillingContractDropDownData(customerId, serviceType),
    onError: (error) => debugConsole(error),
  });
};

export const useGetBilligDefaultContract = (
  customerId: string,
  serviceType: string
) => {
  return useQuery({
    enabled: !!customerId,
    queryKey: ["contracts", "billingDefaultContract", customerId, serviceType],
    queryFn: () => getBilligDefaultContract(customerId, serviceType),
    onSuccess(data) {},
    onError: (error) => debugConsole(error),
  });
};

export const useUpdateServiceContractById = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: {
      serviceId: string;
      serviceType: string;
      contractId: string;
      ticketMappingId?: any;
      projectMappingId?: any;
    }) =>
      updateServiceContractById(
        value.serviceId,
        value.serviceType,
        value.contractId
      ),
    {
      onSuccess(data, variables, context) {
        // queryClient.invalidateQueries(["contracts", "billingDefaultContract"]);
        if (variables?.ticketMappingId) {
          queryClient.invalidateQueries(["ticket", variables?.ticketMappingId]);
        }
        queryClient.invalidateQueries(["readyToBill"]);
        if (variables?.projectMappingId) {
          queryClient.invalidateQueries([
            "project",
            variables?.projectMappingId,
          ]);
        }
      },
    }
  );
};

export const useGetServicesByContractId = (contractId: string) => {
  return useQuery({
    queryKey: ["service_by_contract", contractId],
    queryFn: () => getServicesByContractId(contractId),
    onSuccess(data) {},
    onError: (error) => debugConsole(error),
    enabled: contractId ? true : false,
  });
};

export const useGetServiceEntries = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: GetServiceEntriesInput) => getServiceEntries(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["service_by_contract"]);
      },
    }
  );
};

export const useReassignContract = () => {
  return useMutation(
    (value: ReassignContractInput) => reassignContract(value),
    {}
  );
};

export const useDeleteContract = () => {
  const queryClient = useQueryClient();
  return useMutation((contractId: string) => deleteContract(contractId), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["contracts"]);
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "billing",
        level: "error",
        functionName: "deleteContract",
      }),
  });
};

export const useBulkDeleteContract = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: BulkDeleteContractInput) => bulkDeleteContract(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["contracts"]);
        queryClient.invalidateQueries(["readyToBill"]);
      },
      onError: (error: ApiErrorType) =>
        sentryLogger({
          error: error,
          workflow: "billing",
          level: "error",
          functionName: "bulkDeleteContract",
        }),
    }
  );
};

export const useGetContractsByContractId = (contractId: string) => {
  return useQuery({
    queryKey: ["contracts", contractId],
    queryFn: () => getContractsByContractId(contractId),
    onSuccess(data) {},
    onError: (error) => debugConsole(error),
  });
};

export const useUpdateServiceBillingSettings = () => {
  const queryClient = useQueryClient();
  return useMutation(updateServiceBillingSettings, {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries([
        variables.service_type,
        variables.service_id,
      ]);
      queryClient.invalidateQueries(["readyToBill"]);
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "billing",
        level: "error",
        functionName: "updateServiceBillingSettings",
      }),
  });
};
